import { Check } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui.tsx';
import { stepStateOf } from './logic.ts';

/**
 * Cadastro em etapas (ADR 0128): as etapas em cima, o conteúdo da etapa atual e, no rodapé,
 * Voltar e Continuar (na última, o botão diz o que acontece: "Salvar pedido"). Cada etapa valida
 * antes de seguir; voltar nunca perde o que foi preenchido.
 */
export function StepsLayout({
  steps,
  current,
  onBack,
  onNext,
  finishLabel,
  busy = false,
  children,
}: {
  steps: readonly ReactNode[];
  current: number;
  onBack: () => void;
  onNext: () => void;
  /** O texto do botão da última etapa. */
  finishLabel: string;
  busy?: boolean;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const last = current === steps.length - 1;
  return (
    <div className="ui-panel flex flex-col rounded-lg bg-sheet">
      <ol className="flex flex-wrap gap-x-6 gap-y-2 border-b border-rule px-5 py-3 text-sm">
        {steps.map((step, i) => {
          const state = stepStateOf(i, current);
          return (
            <li
              key={i}
              aria-current={state === 'current' ? 'step' : undefined}
              className={`flex items-center gap-2 ${state === 'next' ? 'text-muted' : 'text-ink'}`}
            >
              <span
                aria-hidden
                className={`flex size-6 items-center justify-center rounded-full text-xs font-semibold tabular-nums ${
                  state === 'done'
                    ? 'bg-accent text-on-accent'
                    : state === 'current'
                      ? 'border-2 border-accent text-brand-text'
                      : 'border border-rule'
                }`}
              >
                {state === 'done' ? <Check size={14} /> : i + 1}
              </span>
              <span className={state === 'current' ? 'font-medium' : ''}>{step}</span>
              {state === 'done' && <span className="sr-only">({t('patterns.steps.done')})</span>}
            </li>
          );
        })}
      </ol>
      <div className="flex flex-col gap-4 p-5">{children}</div>
      <footer className="flex flex-wrap items-center justify-between gap-2 border-t border-rule px-5 py-3">
        <span className="text-xs text-muted">
          {t('patterns.steps.progress', { current: current + 1, total: steps.length })}
        </span>
        <div className="flex gap-2">
          {current > 0 && (
            <Button variant="secondary" onClick={onBack} disabled={busy}>
              {t('patterns.steps.back')}
            </Button>
          )}
          <Button onClick={onNext} disabled={busy}>
            {last ? finishLabel : t('patterns.steps.next')}
          </Button>
        </div>
      </footer>
    </div>
  );
}
